import React, { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import '../styles/CandidateFilterSidebar.css';
import { 
  countriesData, 
  getStatesForCountry,
  getStateCode
} from '../../erprecruitment/config/locationConfig';

const CandidateFilterSidebar = ({
  totalCount,
  overallTotal,
  filters,
  setFilters,
  onClearAll,
  availableLocations = [],
  availableStatuses = [],
  availableRecruiters = [],
  availableWorkTypes = [],
  availableSources = [],
  availableEmploymentTypes = [],
  availableIndustries = [],
  availableWorkAuths = [],
  availableGenders = [],
  onClose,
  visibleColumns = [],
  setVisibleColumns,
  currentView,
  sourceStats = {},
  loading = false,
}) => {
  const [stateSearchQuery, setStateSearchQuery] = useState('');
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef(null);

  const [recruiterSearch, setRecruiterSearch] = useState('');
  const [isRecruiterDropdownOpen, setIsRecruiterDropdownOpen] = useState(false);
  const recruiterDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setIsStateDropdownOpen(false);
      }
      if (recruiterDropdownRef.current && !recruiterDropdownRef.current.contains(event.target)) {
        setIsRecruiterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCheckboxChange = (category, value) => {
    const current = filters[category] || [];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];

    setFilters({ ...filters, [category]: updated });
  };

  const handleRadioChange = (category, value) => {
    setFilters({ ...filters, [category]: value });
  };

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value });
  };

  const handleLocationChange = (field, value) => {
    if (field === 'country') {
      setFilters({
        ...filters,
        country: value,
        states: [],
        cities: [],
      });
      setStateSearchQuery('');
    } else {
      const category = field === 'state' ? 'states' : 'cities';
      const current = filters[category] || [];
      if (value && !current.includes(value)) {
        setFilters({ ...filters, [category]: [...current, value] });
      }
    }
  };

  const handleRemoveLocation = (category, value) => {
    const updated = (filters[category] || []).filter(item => item !== value);
    setFilters({ ...filters, [category]: updated });
  };

  const handleSkillAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const skill = e.target.value.trim();
      const current = filters.skills || [];
      if (!current.includes(skill)) {
        setFilters({ ...filters, skills: [...current, skill] });
      }
      e.target.value = '';
    }
  };

  const handleRemoveSkill = (skill) => {
    const updated = (filters.skills || []).filter(item => item !== skill);
    let newFilters = { ...filters, skills: updated };

    // If the removed tag was a country (by name or code), also clear country/state/city
    const matchedCountry = countriesData.find(c => 
      c.name.toLowerCase() === skill.toLowerCase() || 
      c.code.toLowerCase() === skill.toLowerCase()
    );
    if (matchedCountry && filters.country === matchedCountry.name) {
      newFilters.country = '';
      newFilters.states = [];
      newFilters.cities = [];
    }

    // If the removed tag was a state, also remove it from states array
    if ((filters.states || []).includes(skill)) {
      newFilters.states = (filters.states || []).filter(s => s !== skill);
    }

    setFilters(newFilters);
  };

  // Filter states based on search query
  const filteredStates = filters.country 
    ? getStatesForCountry(countriesData.find(c => c.name === filters.country)?.code).filter(s => {
        const query = stateSearchQuery.toLowerCase();
        const countryCode = countriesData.find(c => c.name === filters.country)?.code;
        const stateCode = getStateCode(countryCode, s)?.toLowerCase() || '';
        return s.toLowerCase().includes(query) || stateCode.includes(query);
      })
    : [];

  const [isLocationExpanded, setIsLocationExpanded] = useState(true);

  return (
    <aside className="candidate-filter-sidebar">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-10px' }}>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
          title="Close Sidebar"
        >
          <X size={18} />
        </button>
      </div>

      {/* Total Count Card */}
      <div className="filter-stats-card">
        <div className="filter-stats-label">Results Found</div>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: '8px 0' }}>
            <div className="cm-skeleton-cell" style={{ height: '32px', width: '90px', borderRadius: '6px' }}></div>
            <div className="cm-skeleton-cell" style={{ height: '14px', width: '140px', borderRadius: '4px' }}></div>
          </div>
        ) : (
          <>
            <div className="filter-stats-value">{totalCount}</div>
            <div className="filter-stats-subtext">out of {overallTotal || totalCount} total records</div>
          </>
        )}
      </div>

      {/* Search Input */}
      {(visibleColumns.includes('Skills') || visibleColumns.includes('Email') || visibleColumns.includes('Phone') || visibleColumns.includes('JobTitle')) && (
        <div className="filter-search-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} className="filter-search-icon" />
            <input
              type="text"
              className="filter-search-input"
              placeholder="Search name, job, skills..."
              value={filters.search || ''}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (filters.search || '').trim()) {
                  const tag = filters.search.trim();
                  let newFilters = { ...filters, search: '' };

                  // Auto-select Country if tag matches a country name OR code (case-insensitive)
                  const matchedCountry = countriesData.find(c => 
                    c.name.toLowerCase() === tag.toLowerCase() || 
                    c.code.toLowerCase() === tag.toLowerCase()
                  );
                  if (matchedCountry) {
                    newFilters.country = matchedCountry.name;
                    newFilters.states = [];
                    newFilters.cities = [];
                  }

                  // Auto-select State if a country is already selected and tag matches a state
                  if (!matchedCountry && (newFilters.country || filters.country)) {
                    const activeCountry = newFilters.country || filters.country;
                    const countryCode = countriesData.find(c => c.name === activeCountry)?.code;
                    const allStates = countryCode ? getStatesForCountry(countryCode) : [];
                    const matchedState = allStates.find(s => s.toLowerCase() === tag.toLowerCase());
                    if (matchedState) {
                      const currentStates = newFilters.states || [];
                      if (!currentStates.includes(matchedState)) {
                        newFilters.states = [...currentStates, matchedState];
                      }
                    }
                  }

                  // Add as a skill/keyword tag
                  const current = newFilters.skills || [];
                  if (!current.includes(tag)) {
                    newFilters.skills = [...current, tag];
                  }

                  setFilters(newFilters);
                }
              }}
            />
          </div>
          
          {/* Keyword Tags */}
          {(filters.skills || []).length > 0 && (
            <div className="filter-tags-container" style={{ marginTop: 0 }}>
              {(filters.skills || []).map(tag => (
                <span 
                  key={tag} 
                  className="filter-tag"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const updated = (filters.skills || []).filter(item => item !== tag);
                    let newFilters = { ...filters, skills: updated, search: tag };
                    
                    const matchedCountry = countriesData.find(c => 
                      c.name.toLowerCase() === tag.toLowerCase() || 
                      c.code.toLowerCase() === tag.toLowerCase()
                    );
                    if (matchedCountry && filters.country === matchedCountry.name) {
                      newFilters.country = '';
                      newFilters.states = [];
                      newFilters.cities = [];
                    }

                    if ((filters.states || []).includes(tag)) {
                      newFilters.states = (filters.states || []).filter(s => s !== tag);
                    }

                    setFilters(newFilters);
                  }}
                >
                  {tag}
                  <X 
                    size={12} 
                    className="filter-tag-close" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSkill(tag);
                    }}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recruiter / Created By Filter */}
      {availableRecruiters && availableRecruiters.length > 0 && visibleColumns.includes('SubmittedBy') && (
        <div className="filter-section" ref={recruiterDropdownRef} style={{ position: 'relative' }}>
          <h4 className="filter-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Created By</span>
            {filters.recruiter && (
              <span
                style={{ fontSize: '11px', color: '#3b5bfd', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setFilters({ ...filters, recruiter: '' })}
              >
                Clear
              </span>
            )}
          </h4>

          {/* Trigger Button */}
          <div
            onClick={() => setIsRecruiterDropdownOpen(!isRecruiterDropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 10px',
              border: filters.recruiter ? '1px solid #3b5bfd' : '1px solid #cbd5e1',
              borderRadius: '6px',
              background: filters.recruiter ? '#eff6ff' : '#ffffff',
              cursor: 'pointer',
              fontSize: '12px',
              color: filters.recruiter ? '#2563eb' : '#334155',
              fontWeight: filters.recruiter ? 600 : 400,
              transition: 'all 0.15s ease'
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {filters.recruiter ? `👤 ${filters.recruiter}` : '🌐 All Recruiters'}
            </span>
            <ChevronDown size={14} style={{ flexShrink: 0, marginLeft: '6px', color: '#64748b' }} />
          </div>

          {/* Searchable Dropdown Menu */}
          {isRecruiterDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                zIndex: 50,
                marginTop: '4px',
                padding: '6px',
                maxHeight: '220px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}
            >
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <input
                  type="text"
                  placeholder="Search recruiter..."
                  value={recruiterSearch}
                  onChange={(e) => setRecruiterSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    fontSize: '11.5px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ overflowY: 'auto', maxHeight: '160px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div
                  onClick={() => {
                    setFilters({ ...filters, recruiter: '' });
                    setIsRecruiterDropdownOpen(false);
                    setRecruiterSearch('');
                  }}
                  style={{
                    padding: '5px 8px',
                    fontSize: '12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    background: !filters.recruiter ? '#f1f5f9' : 'transparent',
                    fontWeight: !filters.recruiter ? 600 : 400,
                    color: !filters.recruiter ? '#0f172a' : '#475569'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = !filters.recruiter ? '#f1f5f9' : 'transparent'}
                >
                  🌐 All Recruiters
                </div>

                {availableRecruiters
                  .filter(r => r.toLowerCase().includes(recruiterSearch.toLowerCase()))
                  .map((recruiter, idx) => {
                    const isSelected = filters.recruiter === recruiter;
                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          setFilters({ ...filters, recruiter });
                          setIsRecruiterDropdownOpen(false);
                          setRecruiterSearch('');
                        }}
                        style={{
                          padding: '5px 8px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          background: isSelected ? '#eff6ff' : 'transparent',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? '#3b5bfd' : '#334155'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#dbeafe' : '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#eff6ff' : 'transparent'}
                      >
                        {recruiter}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Candidate Source Filter: By Recruiter / By Career Portal */}
      {visibleColumns.includes('SourceType') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Candidate Source</h4>
          <div className="filter-options-list">
            {[
              { label: 'All', value: 'all', count: sourceStats?.all },
              { label: 'By Recruiter', value: 'recruiter', count: sourceStats?.recruiter },
              { label: 'By Career Portal', value: 'career_portal', count: sourceStats?.careerPortal }
            ].map(opt => {
              const isSelected = (filters.sourceType || 'all') === opt.value;
              return (
                <label
                  key={opt.value}
                  className="filter-option-item"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '2px 0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="sourceType"
                      checked={isSelected}
                      onChange={() => handleRadioChange('sourceType', opt.value)}
                    />
                    <span
                      className="filter-option-label"
                      style={{
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? '#2563eb' : '#334155'
                      }}
                    >
                      {opt.label}
                    </span>
                  </div>
                  {opt.count !== undefined && (
                    <span style={{
                      fontSize: '11px',
                      color: isSelected ? '#2563eb' : '#64748b',
                      background: isSelected ? '#dbeafe' : '#f1f5f9',
                      padding: '1px 7px',
                      borderRadius: '10px',
                      fontWeight: 600,
                      lineHeight: '1.4'
                    }}>
                      {loading ? '...' : opt.count.toLocaleString()}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Status Filter */}
      {availableStatuses.length > 0 && visibleColumns.includes('CandidateStatus') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Status</h4>
          <div className="filter-options-list">
            {availableStatuses.map(status => (
              <label key={status} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.status || []).includes(status)}
                  onChange={() => handleCheckboxChange('status', status)}
                />
                <span className="filter-option-label">{status}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Person Type Filter */}
      {visibleColumns.includes('PersonType') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Type</h4>
          <div className="filter-options-list">
            {[
              { label: 'All', value: 'all' },
              { label: 'Candidates', value: 'candidates' },
              { label: 'Employees', value: 'employees' }
            ].map(opt => (
              <label key={opt.value} className="filter-option-item">
                <input
                  type="radio"
                  name="personType"
                  checked={(filters.personType || 'all') === opt.value}
                  onChange={() => handleRadioChange('personType', opt.value)}
                />
                <span className="filter-option-label">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Bench Status Filter */}
      {currentView !== 'bench' && visibleColumns.includes('IsBench') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Bench Status</h4>
          <div className="filter-options-list">
            {[
              { label: 'All', value: 'all' },
              { label: 'Bench Only', value: 'bench' },
              { label: 'Non-Bench', value: 'non-bench' }
            ].map(opt => (
              <label key={opt.value} className="filter-option-item">
                <input
                  type="radio"
                  name="isBench"
                  checked={(filters.isBench || 'all') === opt.value}
                  onChange={() => handleRadioChange('isBench', opt.value)}
                />
                <span className="filter-option-label">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Work Type Filter */}
      {availableWorkTypes.length > 0 && visibleColumns.includes('RemoteStatus') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Work Type</h4>
          <div className="filter-options-list">
            {availableWorkTypes.map(type => (
              <label key={type} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.workType || []).includes(type)}
                  onChange={() => handleCheckboxChange('workType', type)}
                />
                <span className="filter-option-label">{type}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Experience Filter */}
      {visibleColumns.includes('Experience') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Experience</h4>
          <div className="filter-options-list">
            {[
              { label: 'All', value: 'all' },
              { label: 'Entry (0-2y)', value: '0-2' },
              { label: 'Mid (3-5y)', value: '3-5' },
              { label: 'Senior (5-10y)', value: '5-10' },
              { label: 'Expert (10y+)', value: '10+' }
            ].map(opt => (
              <label key={opt.value} className="filter-option-item">
                <input
                  type="radio"
                  name="experience"
                  checked={(filters.experience || 'all') === opt.value}
                  onChange={() => handleRadioChange('experience', opt.value)}
                />
                <span className="filter-option-label">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}


      {/* Work Authorization Filter */}
      {availableWorkAuths.length > 0 && visibleColumns.includes('WorkAuthorization') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Work Auth</h4>
          <div className="filter-options-list">
            {availableWorkAuths.map(auth => (
              <label key={auth} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.workAuth || []).includes(auth)}
                  onChange={() => handleCheckboxChange('workAuth', auth)}
                />
                <span className="filter-option-label">{auth}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Employment Type Filter */}
      {availableEmploymentTypes.length > 0 && visibleColumns.includes('EmploymentType') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Emp Type</h4>
          <div className="filter-options-list">
            {availableEmploymentTypes.map(type => (
              <label key={type} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.employmentTypes || []).includes(type)}
                  onChange={() => handleCheckboxChange('employmentTypes', type)}
                />
                <span className="filter-option-label">{type}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Industry Filter */}
      {availableIndustries.length > 0 && visibleColumns.includes('Industry') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Industry</h4>
          <div className="filter-options-list">
            {availableIndustries.map(ind => (
              <label key={ind} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.industries || []).includes(ind)}
                  onChange={() => handleCheckboxChange('industries', ind)}
                />
                <span className="filter-option-label">{ind}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Gender Filter */}
      {availableGenders && availableGenders.length > 0 && visibleColumns.includes('Gender') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Gender</h4>
          <div className="filter-options-list">
            {availableGenders.map(g => (
              <label key={g} className="filter-option-item">
                <input
                  type="checkbox"
                  checked={(filters.gender || []).includes(g)}
                  onChange={() => handleCheckboxChange('gender', g)}
                />
                <span className="filter-option-label">{g}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Relocation Filter */}
      {visibleColumns.includes('Relocation') && (
        <div className="filter-section">
          <h4 className="filter-section-title">Relocation</h4>
          <div className="filter-options-list">
            {[
              { label: 'All', value: 'all' },
              { label: 'Willing', value: 'yes' },
              { label: 'Not Willing', value: 'no' }
            ].map(opt => (
              <label key={opt.value} className="filter-option-item">
                <input
                  type="radio"
                  name="relocate"
                  checked={(filters.relocate || 'all') === opt.value}
                  onChange={() => handleRadioChange('relocate', opt.value)}
                />
                <span className="filter-option-label">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Location Filter */}
      {visibleColumns.includes('CurrentLocation') && (
        <div className="filter-section">
          <div 
            className="filter-section-title" 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setIsLocationExpanded(!isLocationExpanded)}
          >
            Location
            <ChevronDown 
              size={14} 
              style={{ 
                transform: isLocationExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                color: '#94a3b8'
              }} 
            />
          </div>
          {isLocationExpanded && (
            <div className="filter-location-container">
            {/* Country Dropdown */}
            <div className="filter-location-group">
              <label className="filter-location-label">Country</label>
              <select 
                className="filter-select"
                value={filters.country || ''}
                onChange={(e) => handleLocationChange('country', e.target.value)}
              >
                <option value="">Select Country</option>
                {countriesData.map(c => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* State Searchable Input (Multi-select) */}
            <div className="filter-location-group" ref={stateDropdownRef}>
              <label className="filter-location-label">State</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text"
                  className={`filter-location-input ${!filters.country ? 'disabled' : ''}`}
                  style={{ 
                    borderColor: isStateDropdownOpen ? '#3b5bfd' : '#e2e8f0',
                    boxShadow: isStateDropdownOpen ? '0 0 0 2px rgba(59, 91, 253, 0.1)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  placeholder={filters.country ? "Search states" : "Select country first"}
                  value={stateSearchQuery}
                  onChange={(e) => {
                    if (filters.country) {
                      setStateSearchQuery(e.target.value);
                      setIsStateDropdownOpen(true);
                    }
                  }}
                  onFocus={() => {
                    if (filters.country) {
                      setIsStateDropdownOpen(true);
                    }
                  }}
                  disabled={!filters.country}
                />
                <ChevronDown 
                  size={14} 
                  style={{ 
                    position: 'absolute', right: '10px', top: '50%', transform: `translateY(-50%) ${isStateDropdownOpen ? 'rotate(180deg)' : ''}`,
                    color: '#94a3b8', transition: 'transform 0.2s ease', cursor: 'pointer'
                  }}
                  onClick={() => filters.country && setIsStateDropdownOpen(!isStateDropdownOpen)}
                />

                {isStateDropdownOpen && filters.country && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: '#fff', border: '1px solid #3b5bfd',
                    borderRadius: '4px', maxHeight: '180px', overflowY: 'auto',
                    zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    marginTop: '4px'
                  }}>
                    {filteredStates.map(s => (
                      <div 
                        key={s} 
                        className={`filter-dropdown-option ${(filters.states || []).includes(s) ? 'selected' : ''}`}
                        onClick={() => {
                          handleLocationChange('state', s);
                          setIsStateDropdownOpen(false);
                          setStateSearchQuery('');
                        }}
                        style={{
                          padding: '7px 12px', cursor: 'pointer', fontSize: '13px',
                          borderBottom: '1px solid #f3f4f6',
                          background: (filters.states || []).includes(s) ? '#eff6ff' : '#fff',
                          color: '#374151'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={e => e.currentTarget.style.background = (filters.states || []).includes(s) ? '#eff6ff' : '#fff'}
                      >
                        {s}
                      </div>
                    ))}
                    {filteredStates.length === 0 && (
                      <div style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>No states found</div>
                    )}
                  </div>
                )}
              </div>

              {/* State Tags */}
              <div className="filter-tags-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {(filters.states || []).map(s => (
                  <span key={s} className="filter-tag" style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid #bfdbfe' }}>
                    {s}
                    <X 
                      size={12} 
                      style={{ cursor: 'pointer', opacity: 0.7 }}
                      onClick={() => handleRemoveLocation('states', s)}
                    />
                  </span>
                ))}
              </div>
            </div>



            {/* City Input (Multi-select) */}
            <div className="filter-location-group">
              <label className="filter-location-label">City</label>
              <input 
                type="text"
                className="filter-location-input"
                placeholder="Type city & press Enter"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    handleLocationChange('city', e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <div className="filter-tags-container">
                {(filters.cities || []).map(city => (
                  <span key={city} className="filter-tag">
                    {city}
                    <X 
                      size={12} 
                      className="filter-tag-close" 
                      onClick={() => handleRemoveLocation('cities', city)}
                    />
                  </span>
                ))}
              </div>
            </div>
            </div>
          )}
        </div>
      )}

      <button className="clear-filters-btn" onClick={() => {
        onClearAll();
        setStateSearchQuery('');
      }}>
        Clear all filters
      </button>
    </aside>
  );
};

export default CandidateFilterSidebar;
